import { basePath } from '@/next.config'

import logger from '../utils/logger'

export const clientAddTracksToProject = async (
	projectId: string,
	trackIds: number[]
): Promise<ProjectTrack[]> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/tracks`
	const body = trackIds.map((trackId) => ({ trackId }))

	try {
		const response = await fetch(url, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})

		if (!response.ok) {
			throw new Error(response.status.toString())
		}

		const tracks = await response.json()
		if (!tracks) {
			throw new Error('Failed to add tracks to project')
		}

		return tracks
	} catch (error) {
		logger('Error adding tracks to project', error, {
			errorType: 'Project',
			projectId,
			trackIds: trackIds.join(','),
		})
		throw error
	}
}

export const clientDeleteTracksFromProject = async (
	projectId: string,
	trackIds: number[]
): Promise<void> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/tracks`
	try {
		const response = await fetch(url, {
			method: 'DELETE',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ trackIds }),
		})
		if (!response.ok) throw new Error(response.status.toString())
	} catch (error) {
		logger('Error deleting tracks from project', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}

export const clientEditTrackNotes = async (
	projectId: string,
	trackId: number,
	notes: string
): Promise<void> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/tracks`
	try {
		const response = await fetch(url, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify([{ trackId, notes }]),
		})
		if (!response.ok) throw new Error(response.status.toString())
	} catch (error) {
		logger('Error editing track notes', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}

export const clientReorderTracksInProject = async (
	projectId: string,
	trackIds: number[]
): Promise<void> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/tracks/order`
	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ TrackIds: trackIds }),
		})
		if (!response.ok) throw new Error(response.status.toString())
	} catch (error) {
		logger('Error reordering tracks in project', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}

export const clientDeleteTrackFromProject = async (
	projectId: string,
	trackId: number
): Promise<void> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/tracks/${trackId}`
	try {
		const response = await fetch(url, { method: 'DELETE' })
		if (!response.ok) throw new Error(response.status.toString())
	} catch (error) {
		logger('Error deleting track from project', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}

export const clientEditProjectDetails = async (
	projectId: string,
	project: Project
): Promise<boolean> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}`
	const body = {
		Id: project.Id,
		Name: project.Name,
		Description: project.Description,
		IsPublic: project.IsPublic,
	}
	try {
		const response = await fetch(url, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})
		if (!response.ok) throw new Error(response.status.toString())
		return response.status === 204
	} catch (error) {
		logger('Error editing project details', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		return false
	}
}

export const clientDeleteCollaborator = async (
	projectId: string,
	userId: number
): Promise<void> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/collaborators`
	try {
		const response = await fetch(url, {
			method: 'DELETE',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ UserId: userId }),
		})
		if (!response.ok) throw new Error(response.status.toString())
	} catch (error) {
		logger('Error removing collaborator from project', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}

export const clientDuplicateProject = async (
	projectId: string,
	userId: number,
	name?: string
): Promise<Project> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/duplicate`
	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userId, name }),
		})
		if (!response.ok) throw new Error(response.status.toString())
		const project = await response.json()
		return project
	} catch (error) {
		logger('Error duplicating project', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}

export const clientProjectSimilarTracks = async (
	projectId: string
): Promise<Track[]> => {
	const url = `${basePath}/api/musicapp/projects/${projectId}/similar-tracks`
	try {
		const response = await fetch(url)
		if (!response.ok) throw new Error(response.status.toString())

		return await response.json()
	} catch (error) {
		logger('Error retrieving similar tracks', error, {
			errorType: 'Project API',
			fetchUrl: url,
		})
		throw error
	}
}
